import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { userService } from '../../services/userService';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import PersonalInfoSection from './components/PersonalInfoSection';
import LocationSection from './components/LocationSection';
import LanguageSection from './components/LanguageSection';
import PrivacySection from './components/PrivacySection';

const ProfileSettings = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('personal');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({});

  const sections = [
    {
      id: 'personal',
      title: 'Personal Information',
      icon: 'User',
      description: 'Update your basic information and contact details'
    },
    {
      id: 'location',
      title: 'Location Settings',
      icon: 'MapPin',
      description: 'Manage your location and regional preferences'
    },
    {
      id: 'language',
      title: 'Language & Communication',
      icon: 'Globe',
      description: 'Set your language and notification preferences'
    },
    {
      id: 'privacy',
      title: 'Privacy Controls',
      icon: 'Shield',
      description: 'Control your privacy and data sharing settings'
    }
  ];

  // Load user profile data on component mount
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await userService.getUserProfile();

        if (response.success) {
          setFormData(response.data);
        } else {
          if (response.authError) {
            // Redirect to login if authentication failed
            navigate('/login');
            return;
          }
          setError(response.error || 'Failed to load profile data');
        }
      } catch (err) {
        console.error('Error loading profile:', err);
        setError('An unexpected error occurred while loading your profile');
      } finally {
        setIsLoading(false);
      }
    };

    loadUserProfile();
  }, [navigate]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    setError(null);
    setValidationErrors({});

    try {
      const response = await userService.updateUserProfile(formData);

      if (response.success) {
        // Update AuthContext with new user data
        if (updateUser && response.data) {
          updateUser(response.data);
        }

        // Show success message
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);

        console.log('Profile updated successfully:', response.data);
      } else {
        if (response.authError) {
          // Redirect to login if authentication failed
          navigate('/login');
          return;
        }

        if (response.validationErrors) {
          setValidationErrors(response.validationErrors);
          setError('Please fix the validation errors below');
        } else {
          setError(response.error || 'Failed to update profile');
        }
      }
    } catch (err) {
      console.error('Error saving profile:', err);
      setError('An unexpected error occurred while saving your profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/profile-dashboard');
  };

  const renderActiveSection = () => {
    const commonProps = {
      formData,
      onChange: handleInputChange,
      validationErrors,
      isLoading
    };

    switch (activeSection) {
      case 'personal':
        return <PersonalInfoSection {...commonProps} />;
      case 'location':
        return <LocationSection {...commonProps} />;
      case 'language':
        return <LanguageSection {...commonProps} />;
      case 'privacy':
        return <PrivacySection {...commonProps} />;
      default:
        return null;
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20 pb-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center min-h-96">
              <div className="text-center">
                <Icon name="Loader2" size={48} className="text-primary animate-spin mx-auto mb-4" />
                <p className="text-accent-600">Loading your profile...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <Button
                variant="ghost"
                onClick={handleCancel}
                iconName="ArrowLeft"
                className="text-accent-600 hover:text-accent-800"
              />
              <div>
                <h1 className="font-heading text-3xl font-bold text-accent-800">
                  Profile Settings
                </h1>
                <p className="text-accent-600">
                  Manage your account preferences and personal information
                </p>
              </div>
            </div>
          </div>

          {/* Success Message */}
          {showSuccessMessage && (
            <div className="mb-6 p-4 bg-success-50 border border-success-200 rounded-lg flex items-center gap-3">
              <Icon name="CheckCircle" size={20} className="text-success-600" />
              <span className="text-success-800 font-medium">
                Settings updated successfully!
              </span>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-error-50 border border-error-200 rounded-lg flex items-center gap-3">
              <Icon name="AlertCircle" size={20} className="text-error-600" />
              <span className="text-error-800 font-medium">
                {error}
              </span>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Navigation Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-soft p-6 sticky top-24">
                <h2 className="font-heading font-semibold text-accent-800 mb-4">
                  Settings
                </h2>
                <nav className="space-y-2">
                  {sections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full text-left p-3 rounded-lg transition-all duration-200 ${activeSection === section.id
                          ? 'bg-primary-50 text-primary-600 border-l-4 border-primary' : 'text-accent-600 hover:bg-accent-50 hover:text-accent-800'
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon
                          name={section.icon}
                          size={18}
                          className={activeSection === section.id ? 'text-primary' : 'text-accent-500'}
                        />
                        <div>
                          <div className="font-medium">{section.title}</div>
                          <div className="text-sm text-accent-500 mt-1">
                            {section.description}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl shadow-soft p-6 lg:p-8">
                {renderActiveSection()}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-8 flex justify-end gap-4">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveSettings}
              loading={isSaving}
              iconName="Save"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProfileSettings;